/**
 * WebSocket push client (task 20.3, Design: results delivered over WS).
 *
 * Turn latency is unbounded (model time + cold start), so the server never
 * blocks an HTTP response with a turn result — it fans results, DM
 * interjections, AI-PC contributions, writing indicators and retry signals out
 * over the WebSocket push channel. This client:
 *
 *   - connects to the configured `wss://` URL (optionally with a campaign query
 *     param + bearer token so the server can scope the connection),
 *   - parses every inbound frame into a typed {@link RufusPush} and dispatches
 *     it to registered handlers (append to journal, toggle writing indicator,
 *     surface retry — the play view wires these),
 *   - reconnects with capped exponential backoff when the socket drops, so a
 *     transient network blip does not silently stop the push stream.
 *
 * The `WebSocket` constructor is injected so the whole thing is unit-testable
 * with a fake socket — no real network. Timers are injected for the same reason.
 */

import { parsePush, type RufusPush } from "./push-types.js";

/** A socket the client can drive — the subset of the DOM `WebSocket` it uses. */
export interface SocketLike {
  onopen: ((this: unknown, ev: unknown) => unknown) | null;
  onclose: ((this: unknown, ev: unknown) => unknown) | null;
  onerror: ((this: unknown, ev: unknown) => unknown) | null;
  onmessage: ((this: unknown, ev: { data: unknown }) => unknown) | null;
  send(data: string): void;
  close(): void;
}

/** Factory that opens a socket for a URL — `(url) => new WebSocket(url)` in production. */
export type SocketFactory = (url: string) => SocketLike;

export type ConnectionStatus = "connecting" | "open" | "closed";

export interface WsClientOptions {
  /** Base `wss://` URL from runtime config. */
  readonly url: string;
  /** Campaign to scope the connection to; appended as `?campaignId=`. */
  readonly campaignId?: string;
  /**
   * STATIC bearer token appended as `&token=` (back-compat for tests/static
   * callers). Prefer {@link getToken} for live play: a static snapshot captured
   * at construction goes STALE, so a reconnect after the token is refreshed would
   * re-send the expired string (the mid-session "Unauthorized" + stuck
   * "Reconnecting…" bug). When both are set, {@link getToken} wins.
   */
  readonly token?: string;
  /**
   * LIVE bearer-token provider, read fresh on EVERY (re)connect in `buildUrl`.
   * This is the fix for the stale-token reconnect: after a refresh renews the
   * id token, the next reconnect picks it up with no client rebuild. Returns the
   * current id token or null (signed out → the socket reconnects WITHOUT a token
   * and the server denies it, which drives a re-login rather than a silent loop).
   */
  readonly getToken?: () => string | null;
  /**
   * OPTIONAL hook awaited BEFORE a backoff reconnect on a socket that HAD opened
   * (a mid-session drop). Wired to a proactive token refresh ("if the id token is
   * near/at expiry, await a refresh") so a drop caused by an EXPIRED token
   * reconnects with a FRESH one on the first try — without the wasted
   * rejected-handshake round-trip the reactive {@link onUnauthorized} path would
   * otherwise need. Best-effort: a throw/rejection is swallowed (the reconnect
   * still proceeds; {@link onUnauthorized} remains the backstop). Not called on a
   * never-opened handshake rejection — that is the {@link onUnauthorized} path.
   */
  readonly onBeforeReconnect?: () => Promise<void>;
  /** Socket factory (injected for tests). Defaults to the DOM `WebSocket`. */
  readonly socketFactory?: SocketFactory;
  /**
   * OPTIONAL hook invoked when the socket closes WITHOUT ever having opened —
   * the signature of a rejected `$connect` handshake, which for this API means
   * the token query param was missing/expired/invalid (the WS authorizer denied
   * it). It should attempt a token refresh and resolve `true` when a fresh token
   * is available. Called at most once per failed-handshake cycle to avoid a
   * refresh/deny loop; on `true` the caller is expected to reconnect with the
   * new token (in the SPA, refreshing the auth token re-fires the session effect
   * which rebuilds this client with the fresh token). When omitted, a failed
   * handshake just backs off and retries with the same token.
   */
  readonly onUnauthorized?: () => Promise<boolean>;
  /** Base reconnect delay in ms (doubles per attempt, capped). Default 500. */
  readonly baseReconnectDelayMs?: number;
  /** Max reconnect delay in ms. Default 10000. */
  readonly maxReconnectDelayMs?: number;
  /** setTimeout seam (injected for tests). */
  readonly setTimeoutImpl?: (fn: () => void, ms: number) => unknown;
  /** clearTimeout seam (injected for tests). */
  readonly clearTimeoutImpl?: (handle: unknown) => void;
}

/** A handler invoked for every parsed push. */
export type PushHandler = (push: RufusPush) => void;
/** A handler invoked when the connection status changes. */
export type StatusHandler = (status: ConnectionStatus) => void;

const DEFAULT_BASE_DELAY = 500;
const DEFAULT_MAX_DELAY = 10_000;
/**
 * Max consecutive PRE-OPEN refresh-and-reconnect cycles before the client gives
 * up (dual-review fix: bound the loop so a persistently-rejected token cannot
 * storm the token endpoint into a 429 / forced logout). After this many failed
 * refresh-then-handshake attempts without the socket ever opening, STOP (closed)
 * and let the SPA route to login. Reset on a clean open.
 */
const MAX_UNAUTHORIZED_REFRESHES = 5;

export class WsClient {
  private socket: SocketLike | null = null;
  private status: ConnectionStatus = "closed";
  private attempts = 0;
  private stopped = false;
  private reconnectHandle: unknown = null;
  /** True once the CURRENT socket fired `onopen` — distinguishes a live drop
   * from a handshake that was rejected before opening (an auth denial). */
  private opened = false;
  /** Guard so the refresh hook fires at most once per failed-handshake cycle. */
  private unauthorizedAttempted = false;
  /**
   * How many consecutive PRE-OPEN refresh attempts have been made without the
   * socket ever opening (dual-review fix: bound the refresh-and-reconnect loop so
   * a token the authorizer keeps rejecting cannot drive an unbounded refresh storm
   * — e.g. into a Cognito 429 and a forced logout). Reset to 0 on a clean
   * `onopen`; when it reaches {@link MAX_UNAUTHORIZED_REFRESHES} the client STOPS
   * (goes closed) instead of refreshing again.
   */
  private unauthorizedRefreshCount = 0;

  private readonly pushHandlers = new Set<PushHandler>();
  private readonly statusHandlers = new Set<StatusHandler>();

  private readonly socketFactory: SocketFactory;
  private readonly baseDelay: number;
  private readonly maxDelay: number;
  private readonly setTimeoutImpl: (fn: () => void, ms: number) => unknown;
  private readonly clearTimeoutImpl: (handle: unknown) => void;

  constructor(private readonly options: WsClientOptions) {
    this.socketFactory =
      options.socketFactory ??
      ((url) => new WebSocket(url) as unknown as SocketLike);
    this.baseDelay = options.baseReconnectDelayMs ?? DEFAULT_BASE_DELAY;
    this.maxDelay = options.maxReconnectDelayMs ?? DEFAULT_MAX_DELAY;
    this.setTimeoutImpl =
      options.setTimeoutImpl ??
      ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown);
    this.clearTimeoutImpl =
      options.clearTimeoutImpl ??
      ((h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  /** Register a push handler; returns an unsubscribe function. */
  onPush(handler: PushHandler): () => void {
    this.pushHandlers.add(handler);
    return () => this.pushHandlers.delete(handler);
  }

  /** Register a status handler; returns an unsubscribe function. */
  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  /** Current connection status. */
  getStatus(): ConnectionStatus {
    return this.status;
  }

  /** The current LIVE bearer token (getToken) or the static snapshot, or null. */
  private currentToken(): string | null {
    return (this.options.getToken ? this.options.getToken() : this.options.token) ?? null;
  }

  private buildUrl(): string {
    const params = new URLSearchParams();
    if (this.options.campaignId) params.set("campaignId", this.options.campaignId);
    // Read the token LIVE (getToken) so a reconnect after a refresh carries the
    // fresh token; fall back to the static snapshot for back-compat callers.
    const token = this.currentToken();
    if (token) params.set("token", token);
    const qs = params.toString();
    return qs ? `${this.options.url}?${qs}` : this.options.url;
  }

  private setStatus(next: ConnectionStatus): void {
    this.status = next;
    for (const h of this.statusHandlers) h(next);
  }

  /** Open the socket and start listening. Idempotent while already connected. */
  connect(): void {
    this.stopped = false;
    if (this.socket) return;
    this.setStatus("connecting");
    const socket = this.socketFactory(this.buildUrl());
    this.socket = socket;

    this.opened = false;
    socket.onopen = () => {
      this.attempts = 0;
      this.opened = true;
      // A clean open clears the auth-retry guard so a LATER expiry (a drop after
      // a healthy session) can trigger one fresh refresh of its own, and resets
      // the pre-open refresh budget (the loop bound only counts consecutive
      // NEVER-opened cycles).
      this.unauthorizedAttempted = false;
      this.unauthorizedRefreshCount = 0;
      this.setStatus("open");
    };
    socket.onmessage = (ev: { data: unknown }) => {
      const push = parsePush(ev.data);
      if (push) {
        for (const h of this.pushHandlers) h(push);
      }
    };
    socket.onerror = () => {
      // Errors precede a close in the DOM contract; the close handler drives
      // the reconnect. We simply let onclose run.
    };
    socket.onclose = () => {
      const neverOpened = !this.opened;
      this.socket = null;
      if (this.stopped) {
        this.setStatus("closed");
        return;
      }
      this.setStatus("closed");

      // A close WITHOUT a prior open is a rejected handshake — for this API the
      // WS authorizer denied the token (missing/expired/invalid). Try ONE token
      // refresh; on success the client reconnects ITSELF (buildUrl reads the
      // fresh token via `getToken`), so it no longer depends on a React rebuild
      // (which, now that the session effect is keyed on auth PRESENCE not the
      // token VALUE, would not fire on a refresh). On failure, fall back to the
      // normal backoff — with a cleared token the reconnect carries none and the
      // denial drives a re-login rather than a silent same-token loop.
      // BUDGET (dual-review fix): if we have already burned the pre-open refresh
      // budget without ever opening, the token the authorizer keeps rejecting is
      // not going to start working — STOP rather than storm the token endpoint
      // (a 429 → forced logout). The SPA's auth-presence teardown / a manual
      // re-login is the recovery.
      if (neverOpened && this.unauthorizedRefreshCount >= MAX_UNAUTHORIZED_REFRESHES) {
        this.setStatus("closed");
        return;
      }
      if (neverOpened && this.options.onUnauthorized && !this.unauthorizedAttempted) {
        this.unauthorizedAttempted = true;
        this.unauthorizedRefreshCount += 1;
        // Wrap in Promise.resolve().then(...) so a SYNCHRONOUS throw from the hook
        // (not just a rejected promise) is caught — otherwise it would escape
        // onclose unhandled and no reconnect would ever be scheduled (socket stuck
        // closed).
        void Promise.resolve()
          .then(() => this.options.onUnauthorized!())
          .then((refreshed) => {
            if (this.stopped) return;
            if (refreshed) {
              // Token renewed → reconnect immediately (buildUrl reads it live).
              // Re-arm so a reconnect that ALSO fails pre-open can refresh again
              // rather than being permanently locked out (the former one-way-latch
              // bug). scheduleReconnect re-arms on its backoff timer; here we are
              // connecting straight away, so re-arm now.
              this.unauthorizedAttempted = false;
              this.connect();
            } else {
              // Refresh returned false. Distinguish a DEAD session from a TRANSIENT
              // failure by whether a token still exists (auth.ts fix 7 KEEPS the
              // token on a transient error, CLEARS it on a fatal one):
              //   - token GONE → the session is dead; STOP (the SPA's auth-presence
              //     teardown routes to login). No pointless same-token loop.
              //   - token PRESENT → a transient refresh failure; BACKOFF and retry
              //     (scheduleReconnect re-arms the guard on its timer, so the next
              //     delayed attempt refreshes again — spaced, never a storm).
              if (this.currentToken() === null) {
                this.setStatus("closed");
              } else {
                this.scheduleReconnect(false);
              }
            }
          })
          .catch(() => {
            // A thrown hook is treated like a transient failure: back off + retry
            // (bounded) rather than dying, unless the token is already gone.
            if (this.stopped) return;
            if (this.currentToken() === null) this.setStatus("closed");
            else this.scheduleReconnect(false);
          });
        return;
      }

      // A socket that HAD opened and then dropped mid-session: the drop may be an
      // EXPIRED token (API Gateway never re-checks per-frame, so an expiry is only
      // felt on the next handshake). Pass wasOpen so the backoff proactively
      // refreshes BEFORE reconnecting (onBeforeReconnect), so the next `buildUrl`
      // reads a fresh token on the FIRST try — no wasted rejected round-trip.
      this.scheduleReconnect(!neverOpened);
    };
  }

  /**
   * Schedule a backoff reconnect. `wasOpen` is true only for a mid-session drop of
   * a socket that HAD opened — the one case where a proactive pre-reconnect token
   * refresh (`onBeforeReconnect`) is run. A never-opened handshake rejection does
   * NOT run it (its refresh is the `onUnauthorized` path), honoring the hook's
   * documented contract.
   */
  private scheduleReconnect(wasOpen = false): void {
    const delay = Math.min(
      this.maxDelay,
      this.baseDelay * 2 ** this.attempts,
    );
    this.attempts += 1;
    this.reconnectHandle = this.setTimeoutImpl(() => {
      if (this.stopped) return;
      // RE-ARM the auth-retry guard as a NEW delayed attempt begins (fix 3): a
      // handshake that fails pre-open on THIS attempt may then try one refresh
      // again. Re-arming here (on the backoff timer) rather than instantly spaces
      // repeated refresh attempts by the backoff delay — recovery without a storm.
      this.unauthorizedAttempted = false;
      // Proactive pre-reconnect refresh (socket-was-open mid-session drop ONLY):
      // give the auth layer a chance to renew a near/at-expiry token before we
      // rebuild the URL, so the reconnect carries a fresh bearer. Best-effort — a
      // throw/rejection is swallowed and the reconnect proceeds regardless.
      const before = wasOpen ? this.options.onBeforeReconnect : undefined;
      if (before) {
        void Promise.resolve()
          .then(() => before())
          .catch(() => {})
          .finally(() => {
            if (!this.stopped) this.connect();
          });
      } else {
        this.connect();
      }
    }, delay);
  }

  /** Send a raw string over the socket if open (no-op otherwise). */
  send(data: string): void {
    this.socket?.send(data);
  }

  /** Stop reconnecting and close the socket. */
  close(): void {
    this.stopped = true;
    if (this.reconnectHandle != null) {
      this.clearTimeoutImpl(this.reconnectHandle);
      this.reconnectHandle = null;
    }
    this.socket?.close();
    this.socket = null;
    this.setStatus("closed");
  }
}
